<template>
  <div :class="{ 'music-list-collection': variant === 'collection' }">
    <!-- 加载状态 -->
    <template v-if="isLoading">
      <slot name="loading">
        <div class="flex justify-center items-center py-20" role="status" :aria-label="t('music.loading')">
          <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </slot>
    </template>
    
    <!-- 音乐列表 -->
    <template v-else-if="musicList.length > 0">
      <!-- PC端布局 -->
      <div class="music-list-grid hidden sm:grid sm:grid-cols-2 lg:grid-cols-6 md:grid-cols-4 gap-6">
        <div
          v-for="music in musicList"
          :key="music.id"
          class="music-item group rounded-xl overflow-hidden cursor-pointer"
          @click="handleMusicCardClick(music, 'card')"
          @keydown.enter.self.prevent="handleMusicCardClick(music, 'card_keyboard')"
        >
          <!-- 封面图 -->
          <div class="relative aspect-square overflow-hidden group">
            <NuxtLink :to="`/music/song/${music.id}`" :aria-label="music.title || music.metadata?.prompt || t('copyright.untitled')" class="block w-full h-full" @click.stop="trackMusicCardClick(music, 'cover')">
              <CloudflareImg
                :src="music.image_url"
                :alt="music.title"
                rounded="xl"
                class="transition-transform duration-300 group-hover:scale-110"
              />
            </NuxtLink>

            <!-- 播放按钮 -->
            <button type="button" :aria-label="t(isAudioLoading(music) ? 'ux.cancelPlayback' : isPlaying(music) ? 'music.pause' : 'music.play') + ' ' + music.title"
              class="music-list-play" :aria-pressed="isPlaying(music)"
              @click.stop="handleTogglePlayState(music)"
            >
              <div class="relative z-10">
                <div v-if="isAudioLoading(music)" class="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center shadow-lg">
                  <i class="i-lucide-loader-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true"></i>
                </div>
                <div v-else-if="!isPlaying(music)" class="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" viewBox="0 0 24 24" fill="white">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
                <div v-else class="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" viewBox="0 0 24 24" fill="white">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                  </svg>
                </div>
              </div>
            </button>
            <!-- 正在播放指示器 -->
            <div
              v-if="isPlaying(music)"
              class="absolute inset-0 flex items-center justify-center z-20 pointer-events-none group-hover:hidden group-focus-within:hidden"
            >
              <div class="w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
                <div class="sound-wave">
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div class="absolute bottom-0 left-0 right-0 h-1 bg-blue-500">
                <div class="sound-wave-bar w-full h-full"></div>
              </div>
            </div>
          </div>

          <!-- 音乐信息 -->
          <NuxtLink :to="`/music/song/${music.id}`" :aria-label="music.title || music.metadata?.prompt || t('copyright.untitled')" class="block p-4 relative" @click.stop="trackMusicCardClick(music, 'text')">
            <div class="absolute inset-0 bg-gradient-to-l from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div class="relative z-10">
              <h3 class="font-bold text-white truncate hover:text-blue-300" :title="music.title">{{ music.title || music.metadata?.prompt }}</h3>
              <p class="text-gray-400 text-sm mb-2 h-5 truncate overflow-hidden whitespace-nowrap">
                {{ music.metadata && music.metadata.tags ? music.metadata.tags.split(',')[0] : '' }}
              </p>
              <p class="h-6 truncate text-gray-300 rounded-full py-1 text-xs max-w-full overflow-hidden whitespace-nowrap" :title="music.metadata?.tags">
                {{ music.metadata && music.metadata.tags ? music.metadata.tags : '' }}
              </p>
            </div>
          </NuxtLink>
          <button v-if="showCreateSimilar" type="button"
            class="music-list-similar"
            :aria-label="t('music.createSimilar') + ': ' + (music.title || music.metadata?.prompt || '')"
            @click.stop="handleCreateSimilarMusic(music)">
            <span class="i-lucide-copy-plus h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{{ t('music.createSimilar') }}</span>
          </button>
        </div>
      </div>
      
      <!-- 移动端布局（左右结构） -->
      <div class="sm:hidden">
        <div
          v-for="music in musicList"
          :key="music.id"
          class="music-mobile-row flex items-center py-3 hover:bg-gray-700/30 cursor-pointer"
          @click="handleMusicCardClick(music, 'mobile_row')"
          @keydown.enter.self.prevent="handleMusicCardClick(music, 'mobile_row_keyboard')"
        >
          <!-- 封面 -->
          <NuxtLink :to="`/music/song/${music.id}`" :aria-label="music.title || music.metadata?.prompt || t('copyright.untitled')" class="music-mobile-art w-16 h-16 relative rounded-md overflow-hidden mr-3 shrink-0 block" @click.stop="trackMusicCardClick(music, 'mobile_cover')">
            <CloudflareImg
              :src="music.image_url"
              :alt="music.title"
              width="64"
              height="64"
              quality="80"
              rounded="md"
            />

            <!-- 播放状态指示器 -->
            <div v-if="isPlaying(music)" class="absolute inset-0 flex items-center justify-center bg-black/40 z-20">
              <div class="sound-wave-small">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>

            <!-- 时长 -->
            <div class="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/60 rounded text-white text-[10px]">
              {{ formatDuration(music.metadata?.duration) }}
            </div>
          </NuxtLink>

          <!-- 音乐信息 -->
          <NuxtLink :to="`/music/song/${music.id}`" :aria-label="music.title || music.metadata?.prompt || t('copyright.untitled')" class="music-mobile-info flex-1 min-w-0 mr-3 block" @click.stop="trackMusicCardClick(music, 'mobile_text')">
            <h4 class="font-medium text-white truncate" :title="music.title">{{ music.title || music.metadata?.prompt }}</h4>
            <p v-if="variant !== 'collection'" class="text-gray-400 text-xs truncate overflow-hidden whitespace-nowrap">
              {{ music.metadata && music.metadata.tags ? music.metadata.tags.split(',')[0] : '' }}
            </p>
            <p class="truncate text-gray-300 text-xs max-w-full mt-1 overflow-hidden whitespace-nowrap" :title="music.metadata?.tags">
              {{ music.metadata && music.metadata.tags ? music.metadata.tags : '' }}
            </p>
          </NuxtLink>
          <div class="music-mobile-actions flex shrink-0 items-center gap-1">
            <!-- 播放按钮 -->
            <button
              type="button"
              @click.stop="handleTogglePlayState(music)"
              :aria-label="t(isAudioLoading(music) ? 'ux.cancelPlayback' : isPlaying(music) ? 'music.pause' : 'music.play') + ' ' + music.title"
              class="w-11 h-11 rounded-full flex items-center justify-center bg-gray-700/50 hover:bg-blue-600/50"
            >
              <i v-if="isAudioLoading(music)" class="i-lucide-loader-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true"></i>
              <svg v-else-if="isPlaying(music)" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
            <button
              v-if="showCreateSimilar"
              type="button"
              @click.stop="handleCreateSimilarMusic(music)"
              :aria-label="t('music.createSimilar') + ': ' + (music.title || music.metadata?.prompt || '')"
              :title="t('music.createSimilar')"
              class="music-mobile-similar musicmint-icon-action"
            >
              <span v-if="variant !== 'collection'" class="i-lucide-copy-plus h-5 w-5" aria-hidden="true" />
              <span v-else>{{ t('music.createSimilar') }}</span>
            </button>
          </div>
        </div>
      </div>
    </template>
    
    <!-- 无结果 -->
    <template v-else-if="!isLoading && !error && musicList.length === 0">
      <slot name="empty">
        <div class="music-list-empty" role="status">
          <p class="text-lg font-medium text-white">{{ t('music.noResults') }}</p>
          <p v-if="emptyDescription" class="mt-2 text-sm text-gray-400">{{ emptyDescription }}</p>
        </div>
      </slot>
    </template>

    <!-- 加载更多触发器（仅 autoLoadMore 模式） -->
    <div
      v-if="musicList.length > 0 && autoLoadMore !== false"
      v-show="!isLoading"
      ref="loadMoreTrigger"
      class="py-10 flex flex-col items-center justify-center mt-4"
      style="min-height: 100px; position: relative; z-index: 1;"
    >
      <div v-if="error" role="alert" class="music-list-feedback w-full">
        <span class="i-lucide-circle-alert music-list-feedback-icon" aria-hidden="true" />
        <div class="min-w-0">
          <p class="music-list-feedback-title">{{ errorTitle || error }}</p>
          <p v-if="errorDescription" class="music-list-feedback-description">{{ errorDescription }}</p>
          <button type="button" @click="reload" :disabled="isLoading || isLoadingMore" class="musicmint-secondary-button mt-4">{{ t('ux.retry') }}</button>
        </div>
      </div>
      <div v-else-if="isLoadingMore" role="status" class="flex items-center space-x-3">
        <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
        <span class="text-gray-400 text-sm">{{ t('music.loading') }}</span>
      </div>
      <div v-else-if="!hasMore" class="text-gray-400 text-sm">
        {{ t('music.noMoreResults') }}
      </div>
      <button v-else-if="!error" type="button" @click="handleLoadMore" class="min-h-[44px] rounded-lg bg-gray-800 px-5 text-white hover:bg-gray-700">
        {{ t('common.loadMore') }}
      </button>
    </div>

    <!-- 错误信息 -->
    <template v-if="!isLoading && error && (!musicList.length || autoLoadMore === false)">
      <slot name="error" :retry="reload">
        <div role="alert" class="music-list-feedback">
          <span class="i-lucide-circle-alert music-list-feedback-icon" aria-hidden="true" />
          <div class="min-w-0">
            <p class="music-list-feedback-title">{{ errorTitle || error }}</p>
            <p v-if="errorDescription" class="music-list-feedback-description">{{ errorDescription }}</p>
            <button type="button" @click="reload" :disabled="isLoading" class="musicmint-secondary-button mt-4">{{ t('ux.retry') }}</button>
          </div>
        </div>
      </slot>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n';
import { useRouter } from '#imports';
import type { Music } from '~/services/musicService'
import CloudflareImg from '~/components/base/CloudflareImg.vue'
import { useMusicCreate } from '~/composables/useMusicCreate'
import { useToast } from '~/composables/useToast'
import { trackEvent } from '~/utils/analytics'

// 获取国际化函数
const { t } = useI18n()

// 使用音乐交互的composable
const useMusicInteraction = () => {
  const router = useRouter()
  const toast = useToast()
  const { saveMusicData } = useMusicCreate()
  const { t } = useI18n()

  // 播放状态
  const currentPlayingId = ref<string | null>(null)
  const isCurrentlyPlaying = ref(false)
  const isAudioPending = ref(false)

  // 播放音乐
  const playMusic = (music: Music) => {
    console.log('开始播放音乐', {
      musicId: music.id,
      title: music.title
    })
    currentPlayingId.value = music.id
    isCurrentlyPlaying.value = true
    // 触发全局播放事件
    const event = new CustomEvent('play-music', { detail: music })
    window.dispatchEvent(event)
  }

  // 切换播放状态
  const togglePlayState = (music: Music) => {
    console.log('切换播放状态', {
      currentPlayingId: currentPlayingId.value,
      musicId: music.id,
      isCurrentlyPlaying: isCurrentlyPlaying.value
    })
    
    if (currentPlayingId.value === music.id) {
      // 如果是当前正在播放的歌曲，则切换播放/暂停状态
      const shouldPlay = !isCurrentlyPlaying.value && !isAudioPending.value
      console.log('切换当前歌曲的播放状态', { isPlaying: isCurrentlyPlaying.value })
      // 触发全局播放状态切换事件
      const event = new CustomEvent('toggle-play-state', {
        detail: { id: music.id, isPlaying: shouldPlay }
      })
      window.dispatchEvent(event)
    } else {
      // 如果是新的歌曲，则开始播放
      playMusic(music)
    }
  }

  // 检查是否正在播放
  const isPlaying = (music: Music) => {
    const playing = currentPlayingId.value === music.id && isCurrentlyPlaying.value
    return playing
  }
  const isAudioLoading = (music: Music) => currentPlayingId.value === music.id && isAudioPending.value

  // 导航到音乐详情
  const navigateToMusicDetail = (music: Music) => {
    router.push(`/music/song/${music.id}`)
  }

  // 创建同款音乐
  const createSimilarMusic = (music: Music) => {
    if (!music) return
    try {
      // 从音乐元数据中提取歌词信息或使用描述作为歌词
      const lyricsContent = music.metadata?.prompt || ''
      const musicTitle = music.title || ''
      // 确保标签是字符串格式
      const tagsString = typeof music.metadata?.tags === 'string' ? 
                        music.metadata.tags : 
                        (music.metadata?.tags ? String(music.metadata.tags) : '')
      
      // 准备做同款数据
      const similarData = {
        prompt: music.metadata?.prompt || '',
        tags: tagsString,
        instrumental: false,
        lyrics: lyricsContent,
        title: musicTitle,
        mode: 'advanced'
      }
      
      // 保存音乐数据并导航
      saveMusicData(similarData, router.currentRoute.value.path)
      router.push('/create')
    } catch (error) {
      toast.error(t('common.operation_failed'))
    }
  }

  // 监听播放状态变化
  const setupPlayingStateListener = () => {
    const handler = ((e: CustomEvent) => {
      console.log('收到播放状态变化事件', e.detail)
      const { id, isPlaying, isLoading } = e.detail
      currentPlayingId.value = id
      isCurrentlyPlaying.value = isPlaying
      isAudioPending.value = Boolean(isLoading)
    }) as EventListener

    window.addEventListener('playing-state-changed', handler)
    window.dispatchEvent(new Event('request-music-player-state'))
    return () => {
      window.removeEventListener('playing-state-changed', handler)
    }
  }

  return {
    currentPlayingId,
    isCurrentlyPlaying,
    playMusic,
    togglePlayState,
    isPlaying,
    isAudioLoading,
    navigateToMusicDetail,
    createSimilarMusic,
    setupPlayingStateListener
  }
}

const props = defineProps<{
  musicList: Music[]
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  error: string | null
  showCreateSimilar?: boolean
  autoLoadMore?: boolean
  source?: string
  errorTitle?: string
  errorDescription?: string
  emptyDescription?: string
  variant?: 'collection'
}>()

const emit = defineEmits<{
  (e: 'reload'): void
  (e: 'loadMore'): void
}>()

// 使用音乐交互逻辑
const {
  isPlaying,
  isAudioLoading,
  togglePlayState,
  navigateToMusicDetail,
  createSimilarMusic,
  setupPlayingStateListener
} = useMusicInteraction()

const getMusicTrackParams = (music: Music, actionArea: string) => ({
  source: props.source || 'music_list',
  action_area: actionArea,
  song_id: music.id,
  title: music.title || '',
  has_audio: Boolean(music.audio_url),
  has_image: Boolean(music.image_url)
})

const trackMusicCardClick = (music: Music, actionArea: string) => {
  trackEvent('music_card_click', getMusicTrackParams(music, actionArea))
}

const handleMusicCardClick = (music: Music, actionArea: string) => {
  trackMusicCardClick(music, actionArea)
  navigateToMusicDetail(music)
}

const handleTogglePlayState = (music: Music) => {
  trackEvent('music_play_click', {
    ...getMusicTrackParams(music, 'play_button'),
    will_play: !isPlaying(music)
  })
  togglePlayState(music)
}

const handleCreateSimilarMusic = (music: Music) => {
  trackEvent('create_similar_click', getMusicTrackParams(music, 'create_similar'))
  createSimilarMusic(music)
}

// 加载更多触发器
const loadMoreTrigger = ref<HTMLElement | null>(null)
const observer = ref<IntersectionObserver | null>(null)
const loadMoreCooldown = ref(false)

// 处理加载更多
const handleLoadMore = () => {
  if (!props.error && !props.isLoading && !props.isLoadingMore && props.hasMore && !loadMoreCooldown.value) {
    loadMoreCooldown.value = true
    emit('loadMore')
    setTimeout(() => { loadMoreCooldown.value = false }, 1000)
  }
}

// 重新加载
const reload = () => {
  trackEvent('music_list_reload_click', {
    source: props.source || 'music_list'
  })
  emit('reload')
}

// 组件挂载时设置监听器
let cleanup: (() => void) | undefined; // 声明一个变量来存储清理函数

onMounted(() => {
  // 设置播放状态监听器
  cleanup = setupPlayingStateListener()
  
  // 使用 IntersectionObserver 监听加载更多触发器
  // 仅在 autoLoadMore !== false 时设置 IntersectionObserver
  if (props.autoLoadMore !== false) {
    nextTick(() => {
      if (loadMoreTrigger.value) {
        observer.value = new IntersectionObserver((entries) => {
          if (props.error || props.isLoading || props.isLoadingMore || !props.hasMore || props.musicList.length === 0 || loadMoreCooldown.value) {
            return
          }
          const entry = entries[0]
          if (entry.isIntersecting) {
            handleLoadMore()
          }
        }, {
          root: null,
          rootMargin: '100px',
          threshold: 0.1
        })
        observer.value.observe(loadMoreTrigger.value)
      }
    })

    nextTick(() => {
      const documentHeight = document.documentElement.scrollHeight
      const windowHeight = window.innerHeight
      if (!props.error && documentHeight <= windowHeight && props.hasMore && !props.isLoading && !props.isLoadingMore) {
        handleLoadMore()
      }
    })
  }
})

// 组件卸载时清理
onUnmounted(() => {
  if (cleanup) {
    cleanup()
  }
  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }
})

// 监听props变化，重新设置IntersectionObserver（仅 autoLoadMore 模式）
watch(() => [props.musicList.length, props.hasMore], () => {
  if (props.autoLoadMore === false) return

  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }

  nextTick(() => {
    if (loadMoreTrigger.value && props.hasMore) {
      observer.value = new IntersectionObserver((entries) => {
        if (props.error || props.isLoading || props.isLoadingMore || !props.hasMore || props.musicList.length === 0 || loadMoreCooldown.value) {
          return
        }
        const entry = entries[0]
        if (entry.isIntersecting) {
          handleLoadMore()
        }
      }, {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      })
      observer.value.observe(loadMoreTrigger.value)
    }
  })
})

// 格式化时长
const formatDuration = (seconds: number | undefined) => {
  if (!seconds) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
</script>

<style scoped>
.music-list-play { position: absolute; inset-inline-end: 8px; bottom: 8px; z-index: 30; display: flex; width: 44px; height: 44px; align-items: center; justify-content: center; border-radius: 50%; background: #050812cc; color: white; }
.music-list-play:hover { background: #1e40af; }
.music-list-play:focus-visible, .music-list-similar:focus-visible { outline: 2px solid var(--mint-focus); outline-offset: -2px; }
.music-list-similar { display: flex; width: 100%; min-height: 44px; align-items: center; justify-content: flex-start; gap: 8px; padding: 8px 16px; border-radius: 8px; color: #b9c1dc; font-size: 12px; line-height: 1.4; text-align: left; }
.music-list-similar:hover { color: #e0e7ff; background: #818cf80c; }
.music-list-feedback { display: flex; align-items: flex-start; align-self: flex-start; gap: 12px; max-width: 720px; margin-block: 24px; padding: 20px; border: 1px solid var(--mint-border); border-radius: 12px; background: var(--mint-surface); }
.music-list-feedback-icon { flex: none; width: 20px; height: 20px; margin-top: 3px; color: #fda4af; }
.music-list-feedback-title { color: var(--mint-ink); font-size: 16px; font-weight: 550; line-height: 1.5; }
.music-list-feedback-description { max-width: 65ch; margin-top: 6px; color: #b0b6c9; font-size: 14px; line-height: 1.6; }
.music-list-empty { padding-block: 48px; text-align: center; }
.music-list-grid { align-items: start; }
.music-list-collection .music-list-grid { grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 20px; }
@media (max-width: 639px) {
  .music-list-collection .music-mobile-row { display: grid; grid-template-columns: 72px minmax(0, 1fr) 64px; gap: 12px; height: 132px; box-sizing: border-box; padding-block: 16px; border-bottom: 1px solid #37415180; }
  .music-list-collection .music-mobile-art { width: 72px; height: 72px; margin: 0; }
  .music-list-collection .music-mobile-info { margin: 0; max-height: 100px; overflow: hidden; }
  .music-list-collection .music-mobile-info h4 { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; white-space: normal; font-size: 16px; font-weight: 550; line-height: 1.5; overflow-wrap: anywhere; }
  .music-list-collection .music-mobile-info p { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; margin-top: 6px; white-space: normal; color: #b0b6c9; font-size: 13px; line-height: 1.5; overflow-wrap: anywhere; }
  .music-list-collection .music-mobile-actions { flex-direction: column; gap: 4px; align-items: center; }
  .music-list-collection .music-mobile-actions > button { min-height: 44px; }
  .music-list-collection button.music-mobile-similar { width: 64px; height: auto; min-height: 44px; padding: 6px 2px; border-radius: 8px; background: transparent; color: #c7d2fe; font-size: 12px; line-height: 1.5; overflow-wrap: anywhere; }
  .music-list-collection .music-mobile-similar:focus-visible { outline: 2px solid var(--mint-focus); outline-offset: 2px; }
}

/* 声波动画 */
.sound-wave {
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

.sound-wave span {
  display: block;
  width: 2px;
  height: 10px;
  background-color: white;
  border-radius: 1px;
  animation: sound-wave 1.2s infinite ease-in-out;
}

.sound-wave span:nth-child(2) {
  animation-delay: 0.2s;
}

.sound-wave span:nth-child(3) {
  animation-delay: 0.4s;
}

.sound-wave span:nth-child(4) {
  animation-delay: 0.6s;
}

.sound-wave span:nth-child(5) {
  animation-delay: 0.8s;
}

.sound-wave-bar {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
  animation: sound-wave-bar 2s infinite ease-in-out;
}

/* 移动端声波动画 */
.sound-wave-small {
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

.sound-wave-small span {
  display: block;
  width: 2px;
  height: 8px;
  background-color: white;
  border-radius: 1px;
  animation: sound-wave 1.2s infinite ease-in-out;
}

.sound-wave-small span:nth-child(2) {
  animation-delay: 0.2s;
}

.sound-wave-small span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes sound-wave {
  0%, 100% {
    transform: scaleY(0.5);
  }
  50% {
    transform: scaleY(1);
  }
}

@keyframes sound-wave-bar {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
</style>
